import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { CheckCircle, Sparkles } from "lucide-react";
import serviceCeramic from "@/assets/service-ceramic.jpg";

const CeramicCoatingHenderson = () => {
  return (
    <div className="min-h-screen">
      <SEO
        title="Ceramic Coating Henderson NV | Battle Born Protection Films"
        description="Professional ceramic coating for Henderson, NV vehicles. XPEL-grade ceramic and PPF protection. Climate-controlled facility. Call (702) 559-9365."
        canonical="https://battlebornppf.com/ceramic-coating-henderson"
      />
      <link rel="preload" as="image" href={serviceCeramic} />
      {/* Hero Section */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={serviceCeramic} 
            alt="Professional ceramic coating service for Henderson NV vehicles at Battle Born PPF" 
            className="w-full h-full object-cover"
            width="1920"
            height="1080"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/50" />
        </div>
        
        <div className="relative z-10 container mx-auto px-4">
          <div className="max-w-3xl">
            <Sparkles className="w-16 h-16 text-accent mb-6" />
            <h1 className="text-5xl md:text-6xl font-heading font-bold mb-6">
              Ceramic Coating in Henderson
            </h1>
            <p className="text-xl text-muted-foreground">
              Advanced nano-ceramic technology for unmatched gloss, protection, and durability for Henderson vehicles
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-heading font-bold mb-12 text-center">
              Why Ceramic Coating for Henderson Vehicles?
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8 mb-16">
              <div className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-heading font-semibold mb-2">Superior Gloss</h3>
                  <p className="text-muted-foreground">
                    Enhances paint depth and creates a stunning mirror-like finish that lasts for years
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-heading font-semibold mb-2">Hydrophobic Properties</h3>
                  <p className="text-muted-foreground">
                    Water and contaminants bead off instantly, making maintenance effortless
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-heading font-semibold mb-2">Chemical Resistance</h3>
                  <p className="text-muted-foreground">
                    Protects against acid rain, bird droppings, tree sap, and harsh chemicals
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-heading font-semibold mb-2">UV Protection</h3>
                  <p className="text-muted-foreground">
                    Prevents paint oxidation and fading from harmful UV rays
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-heading font-semibold mb-2">Easier Cleaning</h3>
                  <p className="text-muted-foreground">
                    Contaminants wash away easily, reducing wash time and effort
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-heading font-semibold mb-2">Long-Lasting Protection</h3>
                  <p className="text-muted-foreground">
                    Professional-grade coatings last 5-7 years with proper maintenance
                  </p>
                </div>
              </div>
            </div>

            {/* Coating Options */}
            <div className="bg-card border border-border rounded-lg p-8">
              <h3 className="text-2xl font-heading font-bold mb-6">Coating Packages</h3>
              <div className="space-y-4">
                <div className="py-4 border-b border-border">
                  <div className="flex justify-between items-start mb-2">
                    <span className="font-semibold text-lg">Premium Ceramic</span>
                    <span className="text-accent font-semibold">5-Year Warranty</span>
                  </div>
                  <p className="text-muted-foreground text-sm">
                    Professional-grade coating with exceptional gloss and protection
                  </p>
                </div>
                
                <div className="py-4 border-b border-border">
                  <div className="flex justify-between items-start mb-2">
                    <span className="font-semibold text-lg">Elite Ceramic</span>
                    <span className="text-accent font-semibold">7-Year Warranty</span>
                  </div>
                  <p className="text-muted-foreground text-sm">
                    Top-tier coating with enhanced hydrophobic properties and durability
                  </p>
                </div>
                
                <div className="py-4">
                  <div className="flex justify-between items-start mb-2">
                    <span className="font-semibold text-lg">Ultimate Ceramic + PPF</span>
                    <span className="text-accent font-semibold">10-Year Warranty</span>
                  </div>
                  <p className="text-muted-foreground text-sm">
                    Complete protection: PPF coverage with ceramic coating overlay for maximum defense
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-accent/20 to-accent/5">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-heading font-bold mb-6">
            Enhance Your Henderson Vehicle's Beauty
          </h2>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Discover the perfect ceramic coating package for your vehicle
          </p>
          <Button asChild variant="hero" size="lg">
            <Link to="/contact">Request Free Quote</Link>
          </Button>
        </div>
      </section>

      {/* Schema Markup */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "serviceType": "Ceramic Coating in Henderson",
          "provider": {
            "@type": "AutomotiveBusiness",
            "name": "Battle Born Protection Films",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "6960 W Russell Rd B",
              "addressLocality": "Las Vegas",
              "addressRegion": "NV",
              "postalCode": "89113",
              "addressCountry": "US"
            }
          },
          "areaServed": {
            "@type": "City",
            "name": "Henderson"
          },
          "description": "Professional ceramic coating application in Henderson providing superior gloss, hydrophobic protection, and long-lasting durability for your vehicle's finish."
        })}
      </script>
    </div>
  );
};

export default CeramicCoatingHenderson;
